// ============================================
// pages/transportation/AddVehicleForm.tsx — real API create/edit
// ============================================
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, CheckCircle, XCircle, Truck } from 'lucide-react';
import { vehiclesAPI, htContractorsAPI } from '../../api/services';

const empty = {
  vehicle_no: '',
  vehicle_type: 'truck',
  capacity_mts: 0,
  owner_name: '',
  contractor_id: 0,
  driver_name: '',
  driver_phone: '',
  driver_license_no: '',
  registration_date: '',
  insurance_expiry: '',
  fitness_expiry: '',
  permit_expiry: '',
  pollution_expiry: '',
  status: 'active',
  remarks: '',
};

export const AddVehicleForm: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreate = !idParam || idParam === 'add';
  const recordId = isCreate ? null : parseInt(idParam);

  const [contractors, setContractors] = useState<any[]>([]);
  const [form, setForm] = useState<any>(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const c = await htContractorsAPI.list();
        setContractors(Array.isArray(c.data) ? c.data : []);
        if (recordId) {
          const res = await vehiclesAPI.get(recordId);
          setForm({ ...empty, ...res.data });
        }
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
  }, [recordId]);

  const handleChange = (field: string, value: any) => setForm((f: any) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.vehicle_no?.trim()) {
      setMessage({ type: 'error', text: 'Vehicle number is required' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        ...form,
        contractor_id: form.contractor_id || null,
        capacity_mts: form.capacity_mts || null,
        registration_date: form.registration_date || null,
        insurance_expiry: form.insurance_expiry || null,
        fitness_expiry: form.fitness_expiry || null,
        permit_expiry: form.permit_expiry || null,
        pollution_expiry: form.pollution_expiry || null,
      };
      if (isCreate) {
        const res = await vehiclesAPI.create(payload);
        const newId = (res.data as any)?.id;
        setMessage({ type: 'success', text: 'Vehicle saved' });
        if (newId) navigate(`/transportation/vehicles/edit/${newId}`, { replace: true });
      } else if (recordId) {
        await vehiclesAPI.update(recordId, payload);
        setMessage({ type: 'success', text: 'Vehicle updated' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-4xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={() => navigate('/transportation/vehicles')}
        className="mb-4 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg">
        <ArrowLeft className="w-4 h-4" />Back to List
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Truck className="w-7 h-7 text-blue-600" />
          {isCreate ? 'New Vehicle' : `Edit Vehicle ${form.vehicle_no}`}
        </h1>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="mb-3 text-sm font-semibold text-gray-700 border-b pb-2">Vehicle Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle No *</label>
            <input type="text" value={form.vehicle_no} onChange={(e) => handleChange('vehicle_no', e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono font-semibold" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
            <select value={form.vehicle_type} onChange={(e) => handleChange('vehicle_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="truck">Truck</option>
              <option value="mini_truck">Mini Truck</option>
              <option value="heavy_truck">Heavy Truck</option>
              <option value="tractor">Tractor</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Capacity (MT)</label>
            <input type="number" step="0.01" value={form.capacity_mts || ''} onChange={(e) => handleChange('capacity_mts', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Owner Name (if not under a contractor)</label>
            <input type="text" value={form.owner_name || ''} onChange={(e) => handleChange('owner_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Or H&amp;T Contractor</label>
            <select value={form.contractor_id || 0} onChange={(e) => handleChange('contractor_id', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>-- None --</option>
              {contractors.map((c) => (<option key={c.id} value={c.id}>{c.firm_name}</option>))}
            </select>
          </div>
        </div>

        <h3 className="mt-6 mb-3 text-sm font-semibold text-gray-700 border-b pb-2">Driver</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Driver Name</label>
            <input type="text" value={form.driver_name || ''} onChange={(e) => handleChange('driver_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
            <input type="text" value={form.driver_phone || ''} onChange={(e) => handleChange('driver_phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">License No</label>
            <input type="text" value={form.driver_license_no || ''} onChange={(e) => handleChange('driver_license_no', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
          </div>
        </div>

        <h3 className="mt-6 mb-3 text-sm font-semibold text-gray-700 border-b pb-2">Compliance &amp; Expiry</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Registration Date</label>
            <input type="date" value={form.registration_date || ''} onChange={(e) => handleChange('registration_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Insurance Expiry</label>
            <input type="date" value={form.insurance_expiry || ''} onChange={(e) => handleChange('insurance_expiry', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fitness Expiry</label>
            <input type="date" value={form.fitness_expiry || ''} onChange={(e) => handleChange('fitness_expiry', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Permit Expiry</label>
            <input type="date" value={form.permit_expiry || ''} onChange={(e) => handleChange('permit_expiry', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Pollution Expiry</label>
            <input type="date" value={form.pollution_expiry || ''} onChange={(e) => handleChange('pollution_expiry', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select value={form.status} onChange={(e) => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="active">Active</option>
              <option value="maintenance">Maintenance</option>
              <option value="inactive">Inactive</option>
              <option value="blacklisted">Blacklisted</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
          <textarea value={form.remarks || ''} onChange={(e) => handleChange('remarks', e.target.value)} rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isCreate ? 'Save Vehicle' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddVehicleForm;
